import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Storefront, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton, GhostButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BLANK = { business_name: "", email: "", phone: "", city: "", state: "" };

export default function AdminDealers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.get("/admin/dealers").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...BLANK }); setOpen(true); };
  const openEdit = (r) => { setEditing({ ...r }); setOpen(true); };

  const save = async () => {
    if (!editing.business_name.trim()) { toast.error("Business name is required"); return; }
    try {
      if (editing.id) await adminApi.put(`/admin/dealers/${editing.id}`, editing);
      else await adminApi.post("/admin/dealers", editing);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.business_name}"?`)) return;
    await adminApi.delete(`/admin/dealers/${r.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <AdminHeader
        title="Selling Dealers"
        subtitle="Dealers customers can select on the Contact Support page for warranty and parts enquiries"
        actions={<PrimaryButton onClick={openNew} data-testid="dealer-new"><Plus size={16} /> Add Dealer</PrimaryButton>}
      />
      <div className="p-8">
        {loading ? (
          <div className="text-neutral-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-600 border border-dashed border-neutral-800 rounded-xl py-16 text-center flex flex-col items-center gap-3">
            <Storefront size={36} className="text-neutral-700" weight="duotone" /> No dealers yet.
          </div>
        ) : (
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="dealers-table">
              <thead>
                <tr className="text-left text-[0.65rem] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="px-5 py-3 font-semibold">Business</th>
                  <th className="px-5 py-3 font-semibold">Contact</th>
                  <th className="px-5 py-3 font-semibold">Location</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {rows.map((r) => (
                  <tr key={r.id} data-testid="dealer-row" className="hover:bg-white/[0.02]">
                    <td className="px-5 py-4">
                      <div className="font-heading font-semibold text-white">{r.business_name}</div>
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      {r.email && (
                        <div className="flex items-center gap-2">
                          <EnvelopeSimple size={14} className="text-[#73bf44]" /> {r.email}
                        </div>
                      )}
                      {r.phone && (
                        <div className="flex items-center gap-2 mt-1 text-neutral-400">
                          <Phone size={14} className="text-[#73bf44]" /> {r.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-neutral-300">
                      <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-neutral-500" />
                        {[r.city, r.state].filter(Boolean).join(", ") || "—"}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(r)} data-testid="dealer-edit" className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/[0.05]"><PencilSimple size={16} /></button>
                        <button onClick={() => del(r)} data-testid="dealer-delete" className="p-2 rounded-lg text-neutral-400 hover:text-red-400 hover:bg-red-500/10"><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-white">{editing?.id ? "Edit" : "Add"} Dealer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <Field label="Business Name"><input value={editing.business_name} onChange={(e) => setEditing({ ...editing, business_name: e.target.value })} className={inputCls} data-testid="dealer-business" placeholder="Caravan HQ Yatala" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Warranty Email"><input value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={inputCls} data-testid="dealer-email" placeholder="warranty@example.com.au" /></Field>
                <Field label="Phone"><input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={inputCls} data-testid="dealer-phone" placeholder="(07) 3439 8477" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City"><input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} className={inputCls} data-testid="dealer-city" placeholder="Yatala" /></Field>
                <Field label="State"><input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} className={inputCls} data-testid="dealer-state" placeholder="Queensland" /></Field>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={save} data-testid="dealer-save">Save</PrimaryButton>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
